import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { TERMS, type TermsBlock } from '../content/terms';
import { LangToggle, useI18n } from '../i18n';

/**
 * Terms of use — public (no session needed: the sign-in page links here),
 * mounted OUTSIDE the Layout like Login/Verify, so it carries its own
 * language toggle and a back link that depends on whether a session exists.
 */
export function TermsPage(): JSX.Element {
  const { lang, t } = useI18n();
  const { partner } = useAuth();
  const doc = TERMS[lang];
  const back = partner
    ? { to: '/dashboard', label: t('terms.backToPasses') }
    : { to: '/login', label: t('terms.backToLogin') };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <Link to={back.to} className="text-sm text-brand-700 hover:underline">
          {back.label}
        </Link>
        <LangToggle />
      </div>
      <article className="card p-8 sm:p-10">
        <header>
          <h1 className="text-2xl font-bold text-slate-900">{doc.title}</h1>
          <p className="mt-1 text-xs text-slate-500">{doc.effective}</p>
          <p className="mt-4 text-sm text-slate-700 leading-relaxed">{doc.intro}</p>
        </header>
        {doc.sections.map((section) => (
          <section key={section.heading} className="mt-8">
            <h2 className="text-base font-semibold text-slate-900">{section.heading}</h2>
            {section.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </section>
        ))}
      </article>
    </div>
  );
}

function Block({ block }: { block: TermsBlock }): JSX.Element {
  if (block.kind === 'list') {
    return (
      <ul className="mt-3 list-disc pl-5 space-y-1.5 text-sm text-slate-700 leading-relaxed">
        {block.items.map((item) => (
          <li key={item}>{linkifyEmails(item)}</li>
        ))}
      </ul>
    );
  }
  return <p className="mt-3 text-sm text-slate-700 leading-relaxed">{linkifyEmails(block.text)}</p>;
}

const EMAIL_RE = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

/** Splits a run of text on e-mail addresses so each renders as a mailto link. */
function linkifyEmails(text: string): React.ReactNode[] {
  // With a capturing group, split() interleaves the matches at odd indexes.
  return text.split(EMAIL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={`mailto:${part}`} className="text-brand-700 hover:underline">
        {part}
      </a>
    ) : (
      part
    ),
  );
}
